import React, { useState, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import styled from '@emotion/styled';
import * as analytics from '../../analytics';
import { SelectableBusinessUnit } from './types';
import { BusinessUnits, StarSelector, PreTagCheckbox, ProductReviewsCheckbox, SyncReviews } from './components';
import {
  Spinner,
  Button,
  GlobalAlertService,
  colors,
  Typography,
} from '@trustpilot/businessapp-patternlibrary';
import {
  getBusinessUser,
  getBusinessUserAccountNames,
} from '../../api/businessUsersApiClient';
import {
  getIntegrations,
  saveIntegrations,
  checkAccessInfo,
  getOAuthLink,
  getDoesExist,
  syncReviews,
} from '../../api/trustpilotZendeskApiClient';
import { BusinessUser, SettingsInformation } from '../../api/types';
import qs from 'querystring';
import { useParams } from 'react-router-dom';

const api = process.env.REACT_APP_TrustpilotApiUrl!;
const zendeskOauth = process.env.REACT_APP_ZendeskIntegrationUrl!;

const SettingsCard = styled.div`
  border-radius: 3px;
  border: 1px solid ${colors.gray.cardBorder};
  padding: 20px;
  margin-bottom: 24px;
`;

const Title = styled(Typography)`
  margin-bottom: var(--CDS-spacing-m);
`;

interface SettingsProps {
  settings: SettingsInformation;
}

const deriveZendeskAccountName = (businessAccountsNames: string[]) => {
  if (businessAccountsNames.length > 0 && businessAccountsNames.length <= 2) {
    return `Trustpilot ${businessAccountsNames.join(', ')}`;
  }

  return 'Trustpilot account';
};

const useSettingsData = (subdomain: string, settings: SettingsInformation) => {
  const { metadata } = settings;
  const metadataIntegrationSecret = metadata && metadata.integrationSecret;
  const [businessUser, setBusinessUser] = useState<BusinessUser>();
  const [businessAccountsNames, setBusinessAccountsNames] = useState<string[]>(
    []
  );
  const [businessUnits, setBusinessUnits] = useState<SelectableBusinessUnit[]>(
    []
  );
  const [dataStatus, setDataStatus] = useState('none');
  const [selectedStars, setSelectedStars] = useState<number[]>([]);
  const [includePreTaggedReviews, setIncludePreTaggedReviews] = useState<
    boolean
  >(false);
  const [includeProductReviews, setIncludeProductReviews] = useState<
    boolean
  >(false);
  const [doesExist, setDoesExist] = useState<boolean>(false);
  const [useSync, setSync] = useState<boolean>(false);
  const [syncPeriod, setSyncPeriod] = useState<string>('1');

  useEffect(() => {
    setDataStatus('loading');
    const initState = async () => {
      try {
        const businessUser = await getBusinessUser(api);
        if (!businessUser) return;
        setBusinessUser(businessUser);
        analytics.identify(businessUser);

        setDoesExist(await getDoesExist(api, subdomain));

        const integrations = await getIntegrations(
          api,
          businessUser.id,
          subdomain,
          metadataIntegrationSecret
        );
        if (!integrations) return;
        setBusinessUnits(
          integrations.businessUnits.map((businessUnit) => {
            return {
              id: businessUnit.businessUnitId,
              identifyingName: businessUnit.name,
              selected: !!businessUnit && businessUnit.enabled,
              disabled: !!businessUnit && !businessUnit.hasFeature,
            };
          })
        );

        if (integrations.stars) {
          setSelectedStars(integrations?.stars);
        }
        setIncludePreTaggedReviews(!!integrations.preTags);
        setIncludeProductReviews(!!integrations.productReviews);

        const businessAccountsNamesResponse = await getBusinessUserAccountNames(
          api,
          businessUser.id
        );
        if (businessAccountsNamesResponse) {
          setBusinessAccountsNames(businessAccountsNamesResponse);
        }

        // track settings form loaded
        analytics.track('view-admin-ui_zendesksubdomain', {
          zendeskSubdomain: subdomain,
          businessUserId: businessUser.id,
          numberOfBusinessUnits: integrations.businessUnits.filter(
            (businessUnit) => businessUnit.hasFeature
          ).length,
          totalNumberOfBusinessUnits: integrations.businessUnits.length,
        });
      } catch (e) {
        GlobalAlertService.critical(
          'Something went wrong while fetching the settings. Please reload the page and try again.',
          5 * 1000
        );
      }
    };
    initState().then(() => setDataStatus('done'));
  }, [subdomain, metadataIntegrationSecret]);

  const toggleBusinessUnit = (id: string) =>
    setBusinessUnits(
      businessUnits.map((businessUnit) =>
        businessUnit.id === id
          ? {
              ...businessUnit,
              selected: !businessUnit.selected,
            }
          : businessUnit
      )
    );

  const toggleAllBusinessUnits = (selected: boolean) =>
    setBusinessUnits(
      businessUnits.map((businessUnit) =>
        businessUnit.disabled
          ? businessUnit
          : {
              ...businessUnit,
              selected,
            }
      )
    );

  const toggleStar = (star: number) => {
    if (selectedStars.includes(star)) {
      setSelectedStars(
        selectedStars.filter((selectedStar) => selectedStar !== star)
      );
    } else {
      setSelectedStars([...selectedStars, star]);
    }
  };

  const toggleAllStars = (selected: boolean) => {
    const stars = selected ? [1, 2, 3, 4, 5] : [];
    setSelectedStars(stars);
  };

  const toggleIncludePreTaggedReviews = () =>
    setIncludePreTaggedReviews(!includePreTaggedReviews);

  const toggleIncludeProductReviews = () =>
    setIncludeProductReviews(!includeProductReviews);

  const getZendeskIntegrationUrl = async (
    subdomain: string,
    businessUnitIds: string[],
    integrationSecret: string,
    syncPeriod: string | null
  ) => {
    const query = {
      subdomain,
      integrationSecret,
      'businessUnitIds[]': [...businessUnitIds],
      syncPeriod,
    };
    if (zendeskOauth) {
      return await getOAuthLink(`${zendeskOauth}?${qs.stringify(query)}`);
    }
    return '';
  };

  const submitSettings = async (
    settings: SettingsInformation,
    accountName: string
  ) => {
    const { subdomain, pushAccessInformation, metadata } = settings;
    const metadataIntegrationSecret = metadata && metadata.integrationSecret;
    const selectedBusinessUnitIDs = businessUnits
      .filter((item) => item.selected)
      .map((item) => item.id);

    try {
      const businessUnitIdsWithAccess = (
        await Promise.all(
          businessUnits.map(async (businessUnit) => {
            const check = await checkAccessInfo(api, businessUnit.id, subdomain, metadataIntegrationSecret);
            if (check.hasAccess) {
              return businessUnit.id;
            }
            return null;
          })
        )
      ).filter(Boolean) as string[];

      const businessUnitsData = businessUnits.map((item) => ({
        businessUnitId: item.id,
        enabled: item.selected,
      }));
      await saveIntegrations(
        api,
        businessUser!.id,
        subdomain,
        businessUnitsData,
        selectedStars,
        includePreTaggedReviews,
        includeProductReviews,
        pushAccessInformation,
        metadataIntegrationSecret
      );

      // at least one bunit is selected, while no access info is present
      if ((businessUnitIdsWithAccess.length === 0 &&
        selectedBusinessUnitIDs.length > 0) || 
        !metadataIntegrationSecret
      ) {
        const integrationSecret = metadataIntegrationSecret || uuid();
        const data = {
          type: 'oauth',
          oauthUrl: await getZendeskIntegrationUrl(
            subdomain,
            selectedBusinessUnitIDs,
            integrationSecret,
            useSync ? syncPeriod : null
          ),
          metadata: `{ "integrationSecret": "${integrationSecret}", "zendesk_subdomain": "${subdomain}" }`,
          state: '{}',
          accountName,
        };
        window.parent.postMessage(JSON.stringify(data), `*`);
      } else {
        await syncReviews(api, syncPeriod, selectedBusinessUnitIDs, subdomain);
      }

      GlobalAlertService.success('Settings saved successfully.', 3 * 1000);
    } catch (e) {
      GlobalAlertService.critical(
        'Something went wrong while saving the settings. Please reload the page and try again.',
        5 * 1000
      );
    }
  };

  return {
    businessUser,
    businessUnits,
    accountName: deriveZendeskAccountName(businessAccountsNames),
    hasUnavailableBusinessUnits: businessUnits.some(
      (businessUnit) => businessUnit.disabled
    ),
    toggleBusinessUnit,
    toggleAllBusinessUnits,
    loading: dataStatus === 'loading',
    selectedStars,
    toggleStar,
    toggleAllStars,
    includePreTaggedReviews,
    toggleIncludePreTaggedReviews,
    includeProductReviews,
    toggleIncludeProductReviews,
    submitSettings,
    doesExist,
    useSync,
    syncPeriod,
    setSync,
    setSyncPeriod,
  };
};

const Settings = (props: SettingsProps) => {
  const { subdomain, metadata } = props.settings;
  const {
    loading,
    businessUser,
    businessUnits,
    accountName,
    hasUnavailableBusinessUnits,
    toggleBusinessUnit,
    toggleAllBusinessUnits,
    selectedStars,
    toggleStar,
    toggleAllStars,
    includePreTaggedReviews,
    toggleIncludePreTaggedReviews,
    includeProductReviews,
    toggleIncludeProductReviews,
    submitSettings,
    doesExist,
    useSync,
    syncPeriod,
    setSync,
    setSyncPeriod,
  } = useSettingsData(subdomain, props.settings);

  const pathVars = useParams();
  useEffect(() => {
    // @ts-ignore
    const integrationSecret = pathVars && pathVars.integrationSecret;
    if (integrationSecret) {
      // this is first load of the app after the zendesk auth
      // posting a message to the parent to save the integration secret in the metadata
      const zendeskData = {
        type: 'metadata',
        metadata: `{ "integrationSecret": "${integrationSecret}", "zendesk_subdomain": "${subdomain}" }`,
        state: '{}',
        accountName,
      };
      window.parent.postMessage(JSON.stringify(zendeskData), `*`);
    }
  }, [pathVars, metadata, subdomain, accountName]);

  const creatingSecond = doesExist && !(metadata && metadata.integrationSecret);
  const getSelectedBUIDs = () => {
    return businessUnits
      .filter((businessUnit) => businessUnit.selected)
      .map((businessUnit) => businessUnit.id);
  }

  return loading ? (
    <Spinner />
  ) : creatingSecond ? (
    <p>
      You already have one integration, please edit it instead of creating a new
      one
    </p>
  ) : (
    <>
      <Title variant='heading-m'>Integrate Trustpilot reviews with Zendesk</Title>
      <SettingsCard>
        <BusinessUnits
          businessUnits={businessUnits}
          onSelect={toggleBusinessUnit}
          onSelectAll={toggleAllBusinessUnits}
          hasUnavailableBusinessUnits={hasUnavailableBusinessUnits}
        />
        <StarSelector selectedStars={selectedStars} onSelect={toggleStar} onSelectAll={toggleAllStars} />
        <div>
          <PreTagCheckbox
            includePreTaggedReviews={includePreTaggedReviews}
            onChange={toggleIncludePreTaggedReviews}
          />
        </div>
        <div>
          <ProductReviewsCheckbox
            includeProductReviews={includeProductReviews}
            onChange={toggleIncludeProductReviews}
          />
        </div>
      </SettingsCard>
      <SyncReviews toggled={useSync} period={syncPeriod} setToggled={setSync} setSyncPeriod={setSyncPeriod} />
      <Button
        disabled={businessUnits.length === 0 || getSelectedBUIDs().length === 0}
        onClick={() => {
          submitSettings(props.settings, accountName);

          const selectedBusinessUnits = getSelectedBUIDs();
          analytics.track('save-zendesk-settings_zendesksubdomain', {
            zendeskSubdomain: subdomain,
            businessUserId: businessUser?.id,
            selectedBusinessUnits,
            numberOfBusinessUnitsSynchronized: selectedBusinessUnits.length,
            stars: selectedStars,
            includePretags: includePreTaggedReviews,
            includePoductReviews: includeProductReviews,
            syncPeriod: (useSync) ? syncPeriod : undefined,
          });
        }}
      >
        Submit
      </Button>
    </>
  );
};

export default Settings;
